import { getFragment } from '../../graphql';
import type {
    CashOfferInfoFragment,
    InsuranceOfferInfoFragment,
    PharmacyProvider,
    ProviderOffersInfoFragment,
} from '../../graphql/generated/graphql';
import type { OfferFragment } from '../../types';

import { getMinCost, getMinPlanPays } from './get-minimum-cost';

/**
 * Checks if a provider offer has PHX502 coverage restriction code
 * (pharmacy doesn't carry the medication)
 */
export function hasPHX502(
    input: ProviderOffersInfoFragment | OfferFragment
): boolean {
    // Handle provider offers
    if ('offers' in input) {
        return input.offers.some((offer) => {
            const fragmentOffer = getFragment(offer);
            if (fragmentOffer.__typename === 'NonPriceableInsuranceOffer') {
                return fragmentOffer.coverageRestrictionCodes?.some(
                    (code) => code.id === 'PHX502'
                );
            }
            return false;
        });
    }

    // Handle single offer
    if (input.__typename === 'NonPriceableInsuranceOffer') {
        return input.coverageRestrictionCodes?.some(
            (code) => code.id === 'PHX502'
        );
    }
    return false;
}

/**
 * Always sort PHX502 offers to the end
 */
function withPHX502AtEnd(
    compareFn: (
        a: ProviderOffersInfoFragment,
        b: ProviderOffersInfoFragment
    ) => number
) {
    return (a: ProviderOffersInfoFragment, b: ProviderOffersInfoFragment) => {
        const aHasPHX502 = hasPHX502(a);
        const bHasPHX502 = hasPHX502(b);

        if (aHasPHX502 && !bHasPHX502) return 1;
        if (!aHasPHX502 && bHasPHX502) return -1;

        return compareFn(a, b);
    };
}

type SortFunction = (
    offers: ProviderOffersInfoFragment[]
) => ProviderOffersInfoFragment[];

export const sortOptions = {
    distance: 'distance',
    price: 'price',
    planPays: 'planPays',
    deliveryTime: 'deliveryTime',
} as const;

export type SortOption = keyof typeof sortOptions;

function getProviderName(providerOffers: ProviderOffersInfoFragment) {
    const fragment = getFragment(providerOffers.provider);

    switch (fragment.__typename) {
        case 'ChainProvider':
            return fragment.chainName;
        case 'MailOrderProvider':
        case 'PharmacyProvider':
        default:
            return (fragment as PharmacyProvider)?.name ?? 'Unknown';
    }
}

/**
 * Compares two provider offers by pharmacy type (retail vs mail order).
 * Returns null if both are retail (caller should handle retail-specific sorting).
 */
function compareByPharmacyType(
    a: ProviderOffersInfoFragment,
    b: ProviderOffersInfoFragment
): number | null {
    const aIsMailOrder = a.provider?.__typename === 'MailOrderProvider';
    const bIsMailOrder = b.provider?.__typename === 'MailOrderProvider';

    // If one is mail order and one is retail, retail comes first
    if (aIsMailOrder && !bIsMailOrder) {
        return 1; // a (mail order) comes after b (retail)
    }
    if (!aIsMailOrder && bIsMailOrder) {
        return -1; // a (retail) comes before b (mail order)
    }

    // Both are mail order, sort by name
    if (aIsMailOrder && bIsMailOrder) {
        const aName = getProviderName(a);
        const bName = getProviderName(b);
        return aName?.localeCompare(bName);
    }

    // Both are retail - return null to let caller handle
    return null;
}

/**
 * Compares two provider offers by minimum patient cost.
 */
function compareByPatientCost(
    a: ProviderOffersInfoFragment,
    b: ProviderOffersInfoFragment
): number {
    const aMinCost = getMinCost(
        a.offers as (CashOfferInfoFragment | InsuranceOfferInfoFragment)[]
    );
    const bMinCost = getMinCost(
        b.offers as (CashOfferInfoFragment | InsuranceOfferInfoFragment)[]
    );
    return aMinCost - bMinCost;
}

/**
 * Compares two provider offers by minimum plan price.
 */
function compareByPlanPrice(
    a: ProviderOffersInfoFragment,
    b: ProviderOffersInfoFragment
): number {
    const aMinPlanPays = getMinPlanPays(
        a.offers as InsuranceOfferInfoFragment[]
    );
    const bMinPlanPays = getMinPlanPays(
        b.offers as InsuranceOfferInfoFragment[]
    );
    return aMinPlanPays - bMinPlanPays;
}

/**
 * Compares two provider offers by distance (for retail pharmacies).
 * Handles pharmacy type (retail vs mail order) automatically.
 */
function compareByDistance(
    a: ProviderOffersInfoFragment,
    b: ProviderOffersInfoFragment
): number {
    const pharmacyTypeResult = compareByPharmacyType(a, b);
    if (pharmacyTypeResult !== null) {
        return pharmacyTypeResult;
    }

    // Both are retail, sort by distance
    return a.distance - b.distance;
}

/**
 * Sorts an array of provider offers by the minimum amount that the insurance plan pays.
 * Uses a cascading sort: min plan price → min patient cost → min distance
 *
 * @param offers An array of provider offers
 * @returns The sorted array of provider offers by the minimum plan pays amount
 */
export function sortOffersByPlanPays(offers: ProviderOffersInfoFragment[]) {
    return offers.toSorted(
        withPHX502AtEnd((a, b) => {
            // sort: min plan price
            const planPriceResult = compareByPlanPrice(a, b);
            if (planPriceResult !== 0 && !Number.isNaN(planPriceResult)) {
                return planPriceResult;
            }

            // sort: min patient cost (when plan prices are equal)
            const patientCostResult = compareByPatientCost(a, b);
            if (patientCostResult !== 0 && !Number.isNaN(patientCostResult)) {
                return patientCostResult;
            }

            // sort: min distance (when both plan price and patient cost are equal)
            return compareByDistance(a, b);
        })
    );
}

/**
 * Sorts an array of provider offers by the lowest cost between cash and insurance offers.
 * Uses a cascading sort: min patient cost → min plan price → min distance
 *
 * @param offers An array of provider offers
 * @returns The sorted array of provider offers by the lowest cost
 */
export function sortOffersByPrice(offers: ProviderOffersInfoFragment[]) {
    return offers.toSorted(
        withPHX502AtEnd((a, b) => {
            // sort: min patient cost
            const patientCostResult = compareByPatientCost(a, b);
            if (patientCostResult !== 0 && !Number.isNaN(patientCostResult)) {
                return patientCostResult;
            }

            // sort: min plan price (when patient costs are equal)
            const planPriceResult = compareByPlanPrice(a, b);
            if (planPriceResult !== 0 && !Number.isNaN(planPriceResult)) {
                return planPriceResult;
            }

            // sort: min distance (when both patient cost and plan price are equal)
            return compareByDistance(a, b);
        })
    );
}

/**
 * Sorts an array of provider offers by distance.
 * Uses a cascading sort: min distance → min patient cost → min plan price
 *
 * @param offers An array of provider offers
 * @returns The sorted array of provider offers by closest distance to furthest
 */
export function sortOffersByDistance(offers: ProviderOffersInfoFragment[]) {
    return offers.toSorted(
        withPHX502AtEnd((a, b) => {
            // sort: min distance (handles pharmacy type automatically)
            const distanceResult = compareByDistance(a, b);
            if (distanceResult !== 0 && !Number.isNaN(distanceResult)) {
                return distanceResult;
            }

            // sort: min patient cost (when distances are equal)
            const patientCostResult = compareByPatientCost(a, b);
            if (patientCostResult !== 0 && !Number.isNaN(patientCostResult)) {
                return patientCostResult;
            }

            // sort: min plan price (when both distance and patient cost are equal)
            return compareByPlanPrice(a, b);
        })
    );
}

/**
 * Sorts an array of provider offers by delivery time.
 * Prioritizes the highest delivery time (either deliveryDaysMax or deliveryDaysMin if deliveryDaysMax is not
 * available). Offers with null delivery times are treated as Infinity, ensuring they are sorted to the end.
 * @param offers An array of provider offers
 * @returns The sorted array of provider offers by fastest delivery time to slowest
 */

function getMinMaxDeliveryDays(offer: ProviderOffersInfoFragment) {
    let min = Infinity;
    let max = Infinity;
    if (offer.provider?.__typename === 'MailOrderProvider') {
        const fragment = getFragment(offer.provider);
        min = fragment.deliveryDaysMin ?? Infinity;
        max = fragment.deliveryDaysMax ?? Infinity;
    }
    return {
        min,
        max,
    };
}

export function sortOffersByDeliveryTime(offers: ProviderOffersInfoFragment[]) {
    return offers.toSorted(
        withPHX502AtEnd((a, b) => {
            const aDeliveryTimes = getMinMaxDeliveryDays(a);
            const bDeliveryTimes = getMinMaxDeliveryDays(b);

            if (aDeliveryTimes.max === bDeliveryTimes.max) {
                return aDeliveryTimes.min - bDeliveryTimes.min;
            }

            return aDeliveryTimes.max - bDeliveryTimes.max;
        })
    );
}

const sortFunctions: Map<SortOption, SortFunction> = new Map([
    [sortOptions.distance, sortOffersByDistance],
    [sortOptions.deliveryTime, sortOffersByDeliveryTime],
    [sortOptions.planPays, sortOffersByPlanPays],
    [sortOptions.price, sortOffersByPrice],
]);

/**
 * Sorts an array of provider offers based on the selected sorting option.
 *
 * @param offers An array of provider offers
 * @param sortBy The sorting option selected (e.g., distance, delivery time, price)
 * @returns The sorted array of provider offers based on the selected sorting criteria
 */
export function sortProviderOffers(
    offers: ProviderOffersInfoFragment[],
    sortBy: SortOption
): ProviderOffersInfoFragment[] {
    const sortFunction = sortFunctions.get(sortBy);
    return sortFunction ? sortFunction(offers) : offers;
}
